import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { formatRON, imgUrl } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useCategories } from "@/context/CategoriesContext";
import { useWishlist } from "@/context/WishlistContext";
import { useContent } from "@/context/ContentContext";

const ProductCard = ({ product }) => {
  const { lang, t } = useLang();
  const { label } = useCategories();
  const { has, toggle } = useWishlist();
  const content = useContent();
  const [hover, setHover] = useState(false);
  const [ci, setCi] = useState(0);
  const soldOut = product.sizes.reduce((s, x) => s + x.stock, 0) === 0;
  const onSale = !!content?.sales_enabled && (product.sale || (product.old_price && product.old_price > product.price));
  const name = lang === "ro" ? product.name_ro : product.name_en;
  const wished = has(product.slug);

  const col = product.colors[ci] || product.colors[0] || {};
  const colImgs = col.images && col.images.length ? col.images : product.images;
  const img = imgUrl(hover && colImgs[1] ? colImgs[1] : colImgs[0]);
  const showSwatches = product.colors && product.colors.length > 1;

  return (
    <div className="group relative border-b border-r border-border bg-white" data-testid={`product-card-${product.slug}`}>
      <button
        onClick={(e) => { e.preventDefault(); toggle(product.slug); }}
        data-testid={`wishlist-toggle-${product.slug}`}
        className="absolute right-3 top-3 z-10 opacity-60 transition-opacity hover:opacity-100"
        aria-label="wishlist"
      >
        <Heart size={16} fill={wished ? "#0a0a0a" : "none"} />
      </button>
      <Link to={`/product/${product.slug}`} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} className="flex flex-col items-center px-4 pt-4 sm:px-6 sm:pt-6">
        <div className="relative flex aspect-square w-full items-center justify-center overflow-hidden">
          <img src={img} alt={name} loading="lazy" className="max-h-full max-w-full object-contain transition-transform duration-500 ease-out group-hover:scale-105" />
          {soldOut ? (
            <span className="absolute left-0 top-0 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t.product.soldOut}</span>
          ) : onSale ? (
            <span className="absolute left-0 top-0 bg-red-600 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-white" data-testid="sale-badge">SALE!</span>
          ) : null}
        </div>
        <div className="mt-3 flex w-full items-center justify-between">
          <div className="min-w-0">
            <div className="truncate text-xs uc">{name}</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{product.code} · {label(product.category, lang)}</div>
          </div>
          <div className="whitespace-nowrap pl-2 font-mono text-xs" data-testid="card-price">
            {onSale && product.old_price && product.old_price > product.price ? (
              <>
                <span className="mr-1.5 text-muted-foreground line-through">{formatRON(product.old_price)}</span>
                <span>{formatRON(product.price)}</span>
              </>
            ) : formatRON(product.price)}
          </div>
        </div>
      </Link>
      <div className="flex items-center gap-1.5 px-4 pb-4 pt-2 sm:px-6" onMouseLeave={() => setCi(0)}>
        {showSwatches && product.colors.map((c, i) => (
          <button
            key={i}
            onMouseEnter={() => setCi(i)}
            onClick={(e) => { e.preventDefault(); setCi(i); }}
            title={lang === "ro" ? c.name_ro : c.name_en}
            data-testid={`card-color-${product.slug}-${i}`}
            className={`h-4 w-4 border border-border transition-transform hover:scale-110 ${ci === i ? "ring-1 ring-foreground ring-offset-1 ring-offset-white" : ""}`}
            style={{ backgroundColor: c.hex }}
          />
        ))}
      </div>
    </div>
  );
};

export default ProductCard;
